import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  signIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  confirmSignIn,
} from 'aws-amplify/auth';

export interface AuthUser {
  email: string;
  name: string;
  sub: string;
  groups: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSubject = new BehaviorSubject<AuthUser | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(true);

  user$ = this.userSubject.asObservable();
  isLoading$ = this.loadingSubject.asObservable();

  get user(): AuthUser | null {
    return this.userSubject.value;
  }

  get isAuthenticated(): boolean {
    return this.userSubject.value !== null;
  }

  /** True if the user belongs to the 'Admin' Cognito group. */
  get isAdmin(): boolean {
    return this.userSubject.value?.groups.includes('Admin') ?? false;
  }

  constructor() {
    this.checkAuthStatus();
  }

  async checkAuthStatus(): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const timeout = new Promise<null>(resolve => {
        timeoutId = setTimeout(() => resolve(null), 12000);
      });
      this.userSubject.next(await Promise.race([this.restoreUser(), timeout]));
    } catch {
      this.userSubject.next(null);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      this.loadingSubject.next(false);
    }
  }

  private async restoreUser(): Promise<AuthUser | null> {
    const session = await fetchAuthSession();
    if (!session.tokens) return null;

    const currentUser = await getCurrentUser();
    const idToken = session.tokens.idToken;
    const groups = (idToken?.payload?.['cognito:groups'] as string[]) ?? [];
    const email =
      (idToken?.payload?.['email'] as string) ??
      currentUser.signInDetails?.loginId ??
      '';
    const name =
      (idToken?.payload?.['name'] as string) ??
      (idToken?.payload?.['email'] as string) ??
      '';
    return { email, name, sub: currentUser.userId, groups };
  }

  async login(
    email: string,
    password: string
  ): Promise<{ requiresPasswordChange: boolean }> {
    const result = await signIn({ username: email, password });

    if (
      result.nextStep?.signInStep ===
      'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    ) {
      return { requiresPasswordChange: true };
    }

    if (result.isSignedIn) {
      await this.checkAuthStatus();
    }

    return { requiresPasswordChange: false };
  }

  async confirmNewPassword(newPassword: string): Promise<void> {
    await confirmSignIn({ challengeResponse: newPassword });
    await this.checkAuthStatus();
  }

  async logout(): Promise<void> {
    await signOut();
    this.userSubject.next(null);
  }

  /**
   * Returns the Cognito idToken string — used by the HTTP interceptor
   * to attach the Bearer token to every API request.
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }
}
